/**
 * Standard keyboard shortcuts following Google SPA patterns
 *
 * @remarks
 * These constants provide reusable shortcuts aligned with:
 * - Gmail navigation patterns (j/k, g+i, etc.)
 * - Google Drive actions (/, ?, esc)
 * - Google Docs editing (ctrl+s, ctrl+z)
 * - YouTube player controls (space, arrows)
 * - WCAG 2.2 accessibility guidelines
 *
 * @example Basic usage
 * ```typescript
 * import { SHORTCUTS } from './keyboardShortcutConstants'
 * useKeyboardShortcut(SHORTCUTS.SAVE, handleSave)
 * ```
 *
 * @example Platform-aware shortcuts
 * ```typescript
 * // Automatically uses Cmd on Mac, Ctrl on Windows/Linux
 * useKeyboardShortcut(SHORTCUTS.SAVE, handleSave) // ctrl+s or cmd+s
 * ```
 */

// ========================================
// TIER 1: FREQUENT ACTIONS (Google Universal Patterns)
// ========================================

/**
 * Universal patterns found across all Google SPAs
 */
export const UNIVERSAL = {
  /**
   * Focus search input (Gmail, Drive, YouTube style)
   * @example Gmail: / → Focus search
   * @example Drive: / → Focus search
   */
  SEARCH: '/',

  /**
   * Show keyboard shortcuts help (Gmail, Drive style)
   * @example Gmail: ? → Show shortcuts
   * @example Drive: ? → Show shortcuts
   */
  HELP: '?',

  /**
   * Alternative help (Google Docs style)
   * @example Docs: Ctrl+/ → Show shortcuts
   */
  HELP_ALT: 'ctrl+/',

  /**
   * Cancel/Close/Escape (Universal)
   * @example All Google apps: Esc → Close modal/Cancel action
   */
  CANCEL: 'escape',
  CANCEL_ALT: 'esc',

  /**
   * Submit/Send action (Gmail compose, Docs comments)
   * @example Gmail: Ctrl+Enter → Send email
   * @example Docs: Ctrl+Enter → Submit comment
   */
  SUBMIT: 'ctrl+enter',
} as const;

/**
 * CRUD operations (Google Docs/Drive style)
 */
export const CRUD = {
  /**
   * Save document (Google Docs style, universal expectation)
   * @example Docs: Ctrl+S → Save (auto-save, but shortcut exists)
   */
  SAVE: 'ctrl+s',

  /**
   * Create new item
   * @example Drive: Ctrl+N → New document
   */
  CREATE: 'ctrl+n',

  /**
   * Delete item (Gmail, Drive style)
   * @example Gmail: # → Delete
   * @example Drive: # → Delete
   */
  DELETE: '#',
  DELETE_KEY: 'delete',

  /**
   * Edit/Rename (Drive style)
   * @example Drive: n → Rename
   */
  EDIT: 'n',

  /**
   * Duplicate/Copy
   */
  DUPLICATE: 'ctrl+d',
} as const;

/**
 * Undo/Redo (Universal pattern)
 */
export const HISTORY = {
  /**
   * Undo last action (Universal)
   * @example All Google apps: Ctrl+Z → Undo
   */
  UNDO: 'ctrl+z',

  /**
   * Redo last action (Universal)
   * @example All Google apps: Ctrl+Shift+Z → Redo
   */
  REDO: 'ctrl+shift+z',

  /**
   * Alternative redo (Windows style)
   */
  REDO_ALT: 'ctrl+y',
} as const;

/**
 * Navigation - Vim-style (Gmail, Drive, YouTube style)
 *
 * @remarks
 * These shortcuts follow the Vim/Vi editor convention used across Google products.
 * Always provide arrow key alternatives for accessibility.
 */
export const NAVIGATION_VIM = {
  /**
   * Next item (Gmail, Drive style)
   * @example Gmail: j → Next email
   * @example Drive: j → Next file
   * @example YouTube: j → Seek backward 10s
   */
  NEXT: 'j',

  /**
   * Previous item (Gmail, Drive style)
   * @example Gmail: k → Previous email
   * @example Drive: k → Previous file
   * @example YouTube: k → Play/pause
   */
  PREVIOUS: 'k',

  /**
   * Forward (YouTube style)
   * @example YouTube: l → Seek forward 10s
   */
  FORWARD: 'l',

  /**
   * Back (optional, less common)
   */
  BACK: 'h',
} as const;

/**
 * Navigation - Arrow keys (Accessibility alternative)
 *
 * @remarks
 * WCAG 2.2 recommends providing multiple ways to navigate.
 * Arrow keys are more discoverable for non-technical users.
 */
export const NAVIGATION_ARROWS = {
  /**
   * Next item (Accessibility alternative to j)
   * @example All Google apps: ↓ → Next item
   */
  NEXT: 'arrowdown',

  /**
   * Previous item (Accessibility alternative to k)
   * @example All Google apps: ↑ → Previous item
   */
  PREVIOUS: 'arrowup',

  /**
   * Forward (Accessibility alternative to l)
   * @example YouTube: → → Seek forward 5s
   */
  FORWARD: 'arrowright',

  /**
   * Back (Accessibility alternative to h)
   * @example YouTube: ← → Seek backward 5s
   */
  BACK: 'arrowleft',

  /**
   * Jump to start
   */
  HOME: 'home',

  /**
   * Jump to end
   */
  END: 'end',
} as const;

/**
 * Go-to pattern (Gmail style)
 *
 * @remarks
 * Two-key sequences for quick navigation to specific sections.
 * Pattern: g + [destination letter]
 *
 * @example Gmail patterns
 * ```typescript
 * 'g+i' → Go to Inbox
 * 'g+a' → Go to All Mail
 * 'g+s' → Go to Starred
 * ```
 */
export const GO_TO = {
  PREFIX: 'g',

  /**
   * Go to inbox/home (Gmail style)
   * @example Gmail: g+i → Go to Inbox
   */
  INBOX: 'g+i',
  HOME: 'g+h',

  /**
   * Go to all/list
   * @example Gmail: g+a → Go to All Mail
   */
  ALL: 'g+a',
  LIST: 'g+l',

  /**
   * Go to starred/favorites
   * @example Gmail: g+s → Go to Starred
   */
  STARRED: 'g+s',
  FAVORITES: 'g+f',

  /**
   * Go to sent items
   */
  SENT: 'g+t',

  /**
   * Go to drafts
   */
  DRAFTS: 'g+d',
} as const;

/**
 * Selection (Universal)
 */
export const SELECTION = {
  /**
   * Select all (Universal)
   * @example All Google apps: Ctrl+A → Select all
   */
  SELECT_ALL: 'ctrl+a',

  /**
   * Select item (Gmail style)
   * @example Gmail: x → Select conversation
   */
  SELECT: 'x',

  /**
   * Select range
   */
  SELECT_RANGE: 'shift+click',
} as const;

// ========================================
// TIER 2: PRIVILEGED ACTIONS (Admin/Debug)
// ========================================

/**
 * Privileged shortcuts for admin/debug features
 *
 * @remarks
 * These shortcuts use Ctrl+Shift combinations to avoid conflicts
 * and require intentional activation.
 */
export const PRIVILEGED = {
  /**
   * Toggle debug panel
   */
  DEBUG: 'ctrl+shift+d',

  /**
   * Toggle logs
   */
  LOGS: 'ctrl+shift+l',

  /**
   * User admin panel
   */
  ADMIN: 'ctrl+shift+u',

  /**
   * System diagnostics
   */
  DIAGNOSTICS: 'ctrl+shift+y',

  /**
   * Performance monitor
   */
  PERFORMANCE: 'ctrl+shift+p',

  /**
   * Navigation monitors
   */
  MONITORS: 'f9',
} as const;

// ========================================
// TIER 3: EASTER EGGS
// ========================================

/**
 * Easter egg shortcuts for fun/experimental features
 */
export const EASTER_EGGS = {
  /**
   * Konami code sequence
   * @example [↑, ↑, ↓, ↓, ←, →, ←, →, B, A]
   */
  KONAMI: 'konami',

  /**
   * Debug mode
   */
  DEBUG_MODE: 'debug',

  /**
   * Theme switcher easter egg
   */
  SECRET_THEME: 'ctrl+shift+t+h+e+m+e',
} as const;

// ========================================
// COMBINED EXPORTS
// ========================================

/**
 * All standard shortcuts organized by category
 *
 * @example Usage in components
 * ```typescript
 * import { SHORTCUTS } from '@/services/keyboard'
 *
 * // Universal patterns
 * useKeyboardShortcut(SHORTCUTS.SEARCH, handleSearch)
 * useKeyboardShortcut(SHORTCUTS.HELP, handleHelp)
 * useKeyboardShortcut(SHORTCUTS.CANCEL, handleCancel)
 *
 * // CRUD operations
 * useKeyboardShortcut(SHORTCUTS.SAVE, handleSave)
 * useKeyboardShortcut(SHORTCUTS.CREATE, handleCreate)
 *
 * // Navigation
 * useKeyboardShortcut(SHORTCUTS.NAV.VIM.NEXT, handleNext)
 * useKeyboardShortcut(SHORTCUTS.NAV.VIM.PREVIOUS, handlePrevious)
 *
 * // Go-to patterns
 * useKeyboardShortcut(SHORTCUTS.GO_TO.INBOX, () => navigate('/inbox'))
 * ```
 */
export const SHORTCUTS = {
  // Tier 1: Frequent
  ...UNIVERSAL,
  ...CRUD,
  ...HISTORY,
  ...SELECTION,

  // Navigation (with both vim and arrow alternatives)
  NAV: {
    VIM: NAVIGATION_VIM,
    ARROWS: NAVIGATION_ARROWS,
  },

  // Go-to patterns
  GO_TO,

  // Tier 2: Privileged
  PRIVILEGED,

  // Tier 3: Easter eggs
  EASTER_EGGS,
} as const;

/**
 * Platform detection utilities
 */
export const PLATFORM = {
  isMac: () => typeof navigator !== 'undefined' && /Mac|iPhone|iPod|iPad/i.test(navigator.platform),
  isWindows: () => typeof navigator !== 'undefined' && /Win/i.test(navigator.platform),
  isLinux: () => typeof navigator !== 'undefined' && /Linux/i.test(navigator.platform),

  /**
   * Get modifier key name for current platform
   * @returns 'Cmd' on Mac, 'Ctrl' on Windows/Linux
   */
  getModifierKey: () => PLATFORM.isMac() ? 'Cmd' : 'Ctrl',

  /**
   * Get modifier symbol for display
   * @returns '⌘' on Mac, 'Ctrl' on Windows/Linux
   */
  getModifierSymbol: () => PLATFORM.isMac() ? '⌘' : 'Ctrl',
} as const;

/**
 * Helper to convert shortcut pattern to display string
 * @example
 * ```typescript
 * getShortcutDisplay('ctrl+s') // 'Ctrl+S' on Windows, '⌘S' on Mac
 * getShortcutDisplay('g+i') // 'G I'
 * ```
 */
export function getShortcutDisplay(pattern: string): string {
  const isMac = PLATFORM.isMac();

  if (pattern.includes('+') && !pattern.includes('ctrl') && !pattern.includes('shift') && !pattern.includes('alt') && !pattern.includes('meta')) {
    return pattern.toUpperCase().split('+').join(' ');
  }

  let display = pattern;

  if (isMac) {
    display = display
      .replace(/ctrl\+/gi, '⌘')
      .replace(/cmd\+/gi, '⌘')
      .replace(/shift\+/gi, '⇧')
      .replace(/alt\+/gi, '⌥')
      .replace(/meta\+/gi, '⌘');
  } else {
    display = display
      .replace(/ctrl\+/gi, 'Ctrl+')
      .replace(/cmd\+/gi, 'Ctrl+')
      .replace(/shift\+/gi, 'Shift+')
      .replace(/alt\+/gi, 'Alt+');
  }

  const parts = display.split('+');
  if (parts.length > 0) {
    parts[parts.length - 1] = parts[parts.length - 1].toUpperCase();
  }

  return parts.join('+');
}
